import { useState, useEffect, useRef, useCallback } from 'react';
import { X, History, Shield, ShieldCheck, ShieldAlert, Loader2, ExternalLink, Eye, RotateCcw, CheckCircle, XCircle, AlertTriangle, Sparkles, MapPin, User, Users, FileText, Tag, ChevronLeft, Video, Mic, Send, ClipboardList, Bus, Car, PenTool, EyeOff } from 'lucide-react';
import ChargeHistoryTable, { ChargeHistoryData } from '../ChargeHistoryTable';
import { Link } from 'react-router-dom';
import apiClient, { Evidence, Annotation } from '../../api/client';
import { HashDisplay } from '../ui/HashDisplay';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import PDFViewer from './PDFViewer';
import ImageViewer from './ImageViewer';
import AnnotationToolbar, { AnnotationTool } from './AnnotationToolbar';

interface EvidenceViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  evidenceId: string | null;
  onStatusChange?: () => void;
}

export function EvidenceViewerModal({
  isOpen,
  onClose,
  evidenceId,
  onStatusChange,
}: EvidenceViewerModalProps) {
  const { user } = useAuth();
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewLogged, setViewLogged] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    verified: boolean;
    message: string;
  } | null>(null);
  const [confirmRetract, setConfirmRetract] = useState(false);
  const [retracting, setRetracting] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [insightsMode, setInsightsMode] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [videoInsights, setVideoInsights] = useState<any>(null);
  const [insightTab, setInsightTab] = useState<'overview' | 'faces' | 'ocr' | 'objects'>('overview');
  
  // Agent Aegis state
  const [aegisMode, setAegisMode] = useState(false);
  const [aegisMessages, setAegisMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [aegisInput, setAegisInput] = useState('');
  const [aegisLoading, setAegisLoading] = useState(false);

  // Charge History state
  const [chargeHistoryMode, setChargeHistoryMode] = useState(false);
  const [chargeHistoryData, setChargeHistoryData] = useState<ChargeHistoryData | null>(null);
  const [chargeHistoryLoading, setChargeHistoryLoading] = useState(false);

  // Check if user can access Agent Aegis
  const canAccessAegis = user?.role === 'Attorney' || user?.role === 'CaseManager' || user?.role === 'SystemAdmin' || user?.role === 'Judge';
  
  // Charge History: restricted to Attorney (prosecutor) and CaseManager only
  const canAccessChargeHistory = user?.role === 'Attorney' || user?.role === 'CaseManager';

  // Annotation state
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationMode, setAnnotationMode] = useState(false);
  const [activeTool, setActiveTool] = useState<AnnotationTool>(null);
  const [activeColor, setActiveColor] = useState('#FFEB3B');
  const [rectangleFill, setRectangleFill] = useState(false);
  const canAnnotate = user?.role !== 'PublicSubmitter';
  const canRedact = user?.role === 'Attorney' || user?.role === 'CaseManager' || user?.role === 'SystemAdmin';
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState<{ redactedEvidenceId: string; redactionCount: number } | null>(null);

  // Agent Aegis works on all file types via Content Understanding (docs, images, video, audio)
  const isAegisSupported = (mimeType?: string) => {
    if (!mimeType) return false;
    const lower = mimeType.toLowerCase();
    // Support both MIME types (image/png) and short type names (Image)
    return lower.startsWith('application/pdf') ||
      lower.startsWith('application/msword') ||
      lower.startsWith('application/vnd.openxmlformats') ||
      lower.startsWith('text') ||
      lower.startsWith('image') ||
      lower.startsWith('video') ||
      lower.startsWith('audio') ||
      lower === 'pdf' || lower === 'document';
  };

  // Detect RAP sheet by filename patterns used by BSAO / CJIS
  const isRapSheet = (fileName?: string) => {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return lower.includes('rap') || lower.includes('cbf') || lower.includes('cjis') ||
           lower.includes('criminal_history') || lower.includes('criminal history') || 
           lower.includes('charge') || lower.includes('rapsheet');
  };

  useEffect(() => {
    if (isOpen && evidenceId) {
      loadEvidence(evidenceId);
    } else {
      setEvidence(null);
      setViewLogged(false);
      setVerificationResult(null);
      setConfirmRetract(false);
      setShowRejectDialog(false);
      setRejectReason('');
      setInsightsMode(false);
      setVideoInsights(null);
      setInsightTab('overview');
      setAegisMode(false);
      setAegisMessages([]);
      setAegisInput('');
      setAnnotations([]);
      setAnnotationMode(false);
      setActiveTool(null);
    }
  }, [isOpen, evidenceId]);

  // Load annotations when evidence is loaded
  useEffect(() => {
    if (evidence?.id) {
      apiClient.getAnnotations(evidence.id)
        .then(setAnnotations)
        .catch(() => setAnnotations([]));
    }
  }, [evidence?.id]);

  const handleAnnotationCreate = useCallback(async (annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
  }) => {
    if (!evidence) return;
    try {
      const created = await apiClient.createAnnotation(evidence.id, annotation);
      setAnnotations(prev => [...prev, created]);
    } catch (err) {
      console.error('Failed to create annotation:', err);
    }
  }, [evidence?.id]);

  const handleAnnotationDelete = useCallback(async (annotationId: string) => {
    if (!evidence) return;
    try {
      await apiClient.deleteAnnotation(evidence.id, annotationId);
      setAnnotations(prev => prev.filter(a => a.Id !== annotationId));
    } catch (err) {
      console.error('Failed to delete annotation:', err);
    }
  }, [evidence?.id]);

  const handleFinalizeRedactions = useCallback(async () => {
    if (!evidence) return;
    setFinalizing(true);
    setFinalizeResult(null);
    try {
      const result = await apiClient.finalizeRedactions(evidence.id);
      setFinalizeResult({ redactedEvidenceId: result.redactedEvidenceId, redactionCount: result.redactionCount });
    } catch (err) {
      console.error('Failed to finalize redactions:', err);
    } finally {
      setFinalizing(false);
    }
  }, [evidence?.id]);

  const loadEvidence = async (id: string) => {
    setLoading(true);
    try {
      const data = await apiClient.getEvidence(id);
      setEvidence(data);
      
      // Log view event
      try {
        await apiClient.logViewEvent(id);
        setViewLogged(true);
      } catch {
        // View logging might not be available
      }
    } catch (error) {
      console.error('Failed to load evidence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyIntegrity = async () => {
    if (!evidence) return;
    setVerifying(true);
    try {
      const result = await apiClient.verifyIntegrity(evidence.id);
      setVerificationResult({
        verified: result.verified,
        message: result.message,
      });
    } catch {
      setVerificationResult({
        verified: false,
        message: 'Verification request failed',
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleRetract = async () => {
    if (!evidence) return;
    setRetracting(true);
    try {
      await apiClient.retractEvidence(evidence.id);
      setEvidence({ ...evidence, evidenceStatus: 'Retracted' });
      setConfirmRetract(false);
      onStatusChange?.();
    } catch (e) {
      console.error('Retract failed:', e);
    } finally {
      setRetracting(false);
    }
  };

  const handleAccept = async () => {
    if (!evidence) return;
    setAccepting(true);
    try {
      await apiClient.acceptEvidence(evidence.id);
      setEvidence({ ...evidence, evidenceStatus: 'Active' });
      onStatusChange?.();
    } catch (e) {
      console.error('Accept failed:', e);
    } finally {
      setAccepting(false);
    }
  };

  const handleExtractInsights = async () => {
    if (!evidence) return;
    setInsightsMode(true);
    setInsightsLoading(true);
    setInsightTab('overview');
    try {
      const data = await apiClient.getVideoInsights(evidence.id);
      setVideoInsights({ ...data, evidenceId: evidence.id });
    } catch {
      setVideoInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Charge History extraction
  const handleExtractChargeHistory = async () => {
    if (!evidence?.id) return;
    setChargeHistoryMode(true);
    setChargeHistoryLoading(true);
    setChargeHistoryData(null);
    try {
      const data = await apiClient.extractChargeHistory(evidence.id);
      setChargeHistoryData(data);
    } catch (e) {
      console.error('Charge history failed:', e);
    } finally {
      setChargeHistoryLoading(false);
    }
  };

  // Agent Aegis - AI-powered evidence Q&A (document-aware)
  const handleOpenAegis = () => {
    setAegisMode(true);
    setAegisMessages([{
      role: 'assistant',
      content: `Hello, I'm Agent Aegis 🛡️. I'm here to help you understand **${evidence?.title || 'this evidence'}**.\n\nAsk me questions about this document, such as:\n\n• "What are the key findings?"\n• "Who are the people mentioned?"\n• "Summarize the main points"\n• "What dates are referenced?"`
    }]);
  };

  const handleAegisSubmit = async () => {
    if (!aegisInput.trim() || !evidence?.caseNumber || !evidence?.id) return;
    
    const userMessage = aegisInput.trim();
    setAegisInput('');
    setAegisMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAegisLoading(true);
    
    try {
      // Use apiClient (axios) so base URL resolves correctly in all environments.
      // Raw fetch with VITE_API_URL || '/api' breaks on the SWA where VITE_API_URL
      // is undefined at runtime and '/api' has no proxy rule.
      const { answer } = await apiClient.aegisAsk(
        evidence.id,
        userMessage,
        evidence.caseNumber
      );
      setAegisMessages(prev => [...prev, { role: 'assistant', content: answer }]);
    } catch (error) {
      setAegisMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.` 
      }]);
    } finally {
      setAegisLoading(false);
    }
  };

  const handleReject = async () => {
    if (!evidence || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      await apiClient.rejectEvidence(evidence.id, rejectReason);
      setEvidence({ ...evidence, evidenceStatus: 'Rejected' });
      setShowRejectDialog(false);
      setRejectReason('');
      onStatusChange?.();
    } catch (e) {
      console.error('Reject failed:', e);
    } finally {
      setRejecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className={`relative bg-white rounded-xl shadow-xl w-full max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300 ${(insightsMode || aegisMode) ? 'max-w-6xl' : 'max-w-4xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Document Viewer</h2>
            {viewLogged && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                View logged
              </span>
            )}
            {evidence?.evidenceStatus === 'PendingReview' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded border border-amber-200">
                Pending Review
              </span>
            )}
            {evidence?.evidenceStatus === 'Retracted' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded border border-gray-200">
                Retracted
              </span>
            )}
            {evidence?.evidenceStatus === 'Rejected' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded border border-red-200">
                Rejected
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : evidence ? (
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* Preview Area */}
            <div className="flex-1 flex flex-col min-h-64">
              {/* Annotation toolbar */}
              {canAnnotate && isAnnotatable(evidence) && (
                <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-3">
                  <button
                    onClick={() => { setAnnotationMode(!annotationMode); if (annotationMode) setActiveTool(null); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      annotationMode ? 'bg-primary-600 text-white' : 'text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <PenTool className="w-4 h-4" />
                    {annotationMode ? 'Annotating' : 'Annotate'}
                  </button>
                  {annotationMode && (
                    <AnnotationToolbar
                      activeTool={activeTool}
                      onToolChange={setActiveTool}
                      activeColor={activeColor}
                      onColorChange={setActiveColor}
                      rectangleFill={rectangleFill}
                      onRectangleFillChange={setRectangleFill}
                      annotationCount={annotations.length}
                      canRedact={canRedact}
                      compact
                    />
                  )}
                  {annotations.length > 0 && !annotationMode && (
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full">
                        {annotations.filter(a => a.Visibility === 'private').length > 0 && (
                          <span className="text-amber-600">🔒 {annotations.filter(a => a.Visibility === 'private').length} private</span>
                        )}
                        {annotations.filter(a => a.Visibility !== 'private').length > 0 && (
                          <span className="text-blue-600">👥 {annotations.filter(a => a.Visibility !== 'private').length} shared</span>
                        )}
                      </span>
                    </span>
                  )}
                  {/* Redaction count + Finalize button */}
                  {canRedact && annotations.filter(a => a.AnnotationType === 'redaction').length > 0 && (
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <EyeOff className="w-3 h-3" />
                        {annotations.filter(a => a.AnnotationType === 'redaction').length} redaction{annotations.filter(a => a.AnnotationType === 'redaction').length !== 1 ? 's' : ''}
                      </span>
                      {!finalizeResult ? (
                        <button
                          onClick={handleFinalizeRedactions}
                          disabled={finalizing}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {finalizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
                          {finalizing ? 'Finalizing...' : 'Finalize Redacted Copy'}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-lg">
                          <CheckCircle className="w-3 h-3" />
                          Redacted copy created: {finalizeResult.redactedEvidenceId}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 bg-gray-900 flex items-center justify-center">
                <AnnotatablePreview
                  evidence={evidence}
                  annotations={annotations}
                  onAnnotationCreate={handleAnnotationCreate}
                  onAnnotationDelete={handleAnnotationDelete}
                  activeTool={annotationMode ? activeTool : null}
                  activeColor={activeColor}
                  rectangleFill={rectangleFill}
                  readOnly={!canAnnotate}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className={`w-full border-t md:border-t-0 md:border-l border-gray-200 overflow-y-auto ${(insightsMode || aegisMode || chargeHistoryMode) ? 'md:w-[480px]' : 'md:w-80'}`}>
              {insightsMode ? (
                <InsightsPanel
                  loading={insightsLoading}
                  insights={videoInsights}
                  activeTab={insightTab}
                  onTabChange={setInsightTab}
                  onClose={() => { setInsightsMode(false); setVideoInsights(null); }}
                />
              ) : aegisMode ? (
                <AgentAegisPanel
                  messages={aegisMessages}
                  input={aegisInput}
                  loading={aegisLoading}
                  onInputChange={setAegisInput}
                  onSubmit={handleAegisSubmit}
                  onClose={() => { setAegisMode(false); setAegisMessages([]); }}
                />
              ) : chargeHistoryMode ? (
                <ChargeHistoryTable
                  data={chargeHistoryData!}
                  evidenceTitle={evidence?.title || ''}
                  loading={chargeHistoryLoading}
                  onClose={() => { setChargeHistoryMode(false); setChargeHistoryData(null); }}
                />
              ) : (
              <div className="p-4 space-y-4">
                {/* File Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">File Information</h3>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500">Filename</p>
                      <p className="text-sm font-medium text-gray-900 break-all">{evidence.fileName}</p>
                    </div>
                    <div className="flex gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Type</p>
                        <p className="text-sm text-gray-900">{evidence.evidenceType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Size</p>
                        <p className="text-sm text-gray-900">{formatFileSize(evidence.fileSize)}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Uploaded</p>
                      <p className="text-sm text-gray-900">
                        {format(new Date(evidence.uploadedAt), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {evidence.description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                    <p className="text-sm text-gray-700">{evidence.description}</p>
                  </div>
                )}

                {/* TODO: Restore AI Analysis status badge once AiExtracted mapping is reliable.
                     Currently shows "Pending AI Processing" for most items even after extraction.
                     Removed temporarily to avoid confusing demo audiences.
                     See: EvidenceViewerModal AI Processing Status block */}

                {/* Integrity Status */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Integrity</h3>
                  <div className="space-y-3">
                    <HashDisplay
                      hash={evidence.sha256Hash}
                      verified={verificationResult?.verified ?? (evidence.integrityStatus === 'verified')}
                      size="sm"
                    />
                    
                    <button
                      onClick={handleVerifyIntegrity}
                      disabled={verifying}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
                    >
                      {verifying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : verificationResult?.verified === true ? (
                        <ShieldCheck className="h-4 w-4 text-green-600" />
                      ) : verificationResult?.verified === false ? (
                        <ShieldAlert className="h-4 w-4 text-red-600" />
                      ) : (
                        <Shield className="h-4 w-4" />
                      )}
                      {verificationResult ? verificationResult.message : 'Verify Integrity'}
                    </button>
                  </div>
                </div>

                {/* Legal Hold */}
                {evidence.legalHold && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                    <Shield className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Under Legal Hold</span>
                  </div>
                )}

                {/* Redaction Summary */}
                {canRedact && annotations.filter(a => a.AnnotationType === 'redaction').length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Redaction Summary</h3>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-red-800 inline-flex items-center gap-1">
                          <EyeOff className="w-3 h-3" />
                          {annotations.filter(a => a.AnnotationType === 'redaction').length} Redaction{annotations.filter(a => a.AnnotationType === 'redaction').length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-red-600">
                          {annotations.filter(a => a.AnnotationType === 'redaction' && a.Content?.startsWith('Finalized:')).length > 0 ? 'Finalized' : 'Draft'}
                        </span>
                      </div>
                      {annotations.filter(a => a.AnnotationType === 'redaction').map((r, i) => (
                        <div key={r.Id} className="text-xs text-red-700 flex items-center justify-between border-t border-red-100 pt-1">
                          <span>Redaction {i + 1}{r.PageNumber ? ` (Page ${r.PageNumber})` : ''}</span>
                          <span className="text-red-500">{r.UserName?.split(',')[0]}</span>
                        </div>
                      ))}
                      {finalizeResult && (
                        <div className="border-t border-red-200 pt-2 mt-1">
                          <div className="text-xs text-green-700 bg-green-50 rounded p-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 flex-shrink-0" />
                            <div>
                              <p className="font-medium">Redacted copy created</p>
                              <p className="text-green-600 break-all">{finalizeResult.redactedEvidenceId}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
              )} {/* end insights mode toggle */}

              {/* Evidence Intake workflow actions — shown outside insights mode only */}
              {!insightsMode && !aegisMode && evidence.evidenceStatus === 'PendingReview' && (
                  <div className="space-y-2 p-3 m-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Awaiting Intake Review</span>
                    </div>
                    <p className="text-xs text-amber-700">
                      Not yet in chain of custody. Awaiting case manager acceptance.
                    </p>
                    {user?.email === evidence.uploadedBy && (
                      confirmRetract ? (
                        <div className="flex gap-2">
                          <button onClick={handleRetract} disabled={retracting}
                            className="flex-1 flex justify-center items-center gap-1 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50">
                            {retracting && <Loader2 className="h-3 w-3 animate-spin" />}Confirm Retract
                          </button>
                          <button onClick={() => setConfirmRetract(false)}
                            className="flex-1 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmRetract(true)}
                          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded hover:bg-amber-50">
                          <RotateCcw className="h-3 w-3" />Retract Upload
                        </button>
                      )
                    )}
                    {(user?.role === 'CaseManager' || user?.role === 'SystemAdmin') && (
                      <div className="flex gap-2">
                        <button onClick={handleAccept} disabled={accepting}
                          className="flex-1 flex justify-center items-center gap-1 py-1.5 text-xs font-medium text-green-700 bg-white border border-green-300 rounded hover:bg-green-50 disabled:opacity-50">
                          {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}Accept
                        </button>
                        <button onClick={() => setShowRejectDialog(true)}
                          className="flex-1 flex justify-center items-center gap-1 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50">
                          <XCircle className="h-3 w-3" />Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-12 text-gray-500">
            Evidence not found
          </div>
        )}

        {/* Footer */}
        {evidence && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <Link
              to={`/custody/${evidence.id}`}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <History className="h-4 w-4" />
              View Custody Chain
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              {evidence?.fileType?.toLowerCase().includes('video') && !insightsMode && !aegisMode && (
                <button
                  onClick={handleExtractInsights}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
                >
                  <Sparkles className="h-4 w-4" />
                  Extract Insights
                </button>
              )}
              {canAccessChargeHistory && !aegisMode && !insightsMode && !chargeHistoryMode && isRapSheet(evidence?.fileName) && (
                <button
                  onClick={handleExtractChargeHistory}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  <ClipboardList className="h-4 w-4" />
                  Charge History
                </button>
              )}
              {canAccessAegis && !aegisMode && !insightsMode && !chargeHistoryMode && isAegisSupported(evidence?.fileType) && (
                <button
                  onClick={handleOpenAegis}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                >
                  <Shield className="h-4 w-4" />
                  Agent Aegis
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reject reason dialog */}
      {showRejectDialog && evidence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Reject Evidence</h3>
            <p className="text-sm text-gray-500">Provide a reason — this will be recorded in the custody audit trail.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Incorrect case number, duplicate submission, not relevant to case..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejecting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rejecting && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EvidencePreview({ evidence }: { evidence: Evidence }) {
  const fileType = evidence.fileType?.toLowerCase() || '';
  const fileName = evidence.fileName?.toLowerCase() || '';
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);

  const isImage = fileType.includes('image') && !fileType.includes('heic') && !fileType.includes('heif');
  const isVideo = fileType.includes('video');
  const isPdf = fileType.includes('pdf') || fileName.endsWith('.pdf');
  const isAudio = fileType.includes('audio') || fileName.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/i) !== null;
  const canPreview = isImage || isVideo || isPdf || isAudio;

  useEffect(() => {
    // Only fetch from backend when there's no external URL and it's a previewable type
    if (!evidence.externalUrl && !evidence.thumbnailUrl && (canPreview || isAudio) && evidence.blobPath) {
      apiClient.getPreviewBlobUrl(evidence.id)
        .then(url => setBlobUrl(url))
        .catch(() => setPreviewError(true));
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [evidence.id]);

  // External URL preview (demo / seeded data)
  const previewUrl = evidence.externalUrl || evidence.thumbnailUrl || blobUrl;

  if (previewUrl) {
    if (isImage || (!isVideo && (previewUrl === evidence.externalUrl || previewUrl === evidence.thumbnailUrl) && evidence.externalUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i))) {
      return (
        <img
          src={previewUrl}
          alt={evidence.fileName}
          className="max-w-full max-h-full object-contain"
        />
      );
    }
    if (isVideo) {
      return (
        <video
          src={previewUrl}
          controls
          className="max-w-full max-h-full"
        />
      );
    }
    if (isAudio) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
          <div className="text-8xl">🎵</div>
          <p className="text-white text-sm font-medium">{evidence.fileName}</p>
          <audio
            src={previewUrl}
            controls
            className="w-full max-w-md"
            style={{ colorScheme: 'dark' }}
          />
        </div>
      );
    }
    if (isPdf) {
      return (
        <iframe
          src={previewUrl}
          title={evidence.fileName}
          className="w-full h-full min-h-[500px] bg-white rounded"
          style={{ border: 'none' }}
        />
      );
    }
    // Fallback for image type with any URL
    return (
      <img
        src={previewUrl}
        alt={evidence.fileName}
        className="max-w-full max-h-full object-contain"
      />
    );
  }

  // Loading state for real uploads
  if (canPreview && evidence.blobPath && !previewError && !blobUrl) {
    return (
      <div className="text-center text-gray-400 p-8">
        <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4" />
        <p className="text-sm">Loading preview...</p>
      </div>
    );
  }

  // Generic file preview
  return (
    <div className="text-center text-gray-400 p-8">
      <div className="text-6xl mb-4">
        {fileType.includes('video') ? '🎬' :
         fileType.includes('image') ? '🖼️' :
         fileType.includes('audio') ? '🎵' :
         fileType.includes('pdf') ? '📄' : '📁'}
      </div>
      <p className="text-lg font-medium">{evidence.fileName}</p>
      <p className="text-sm mt-2">Preview not available</p>
      {evidence.externalUrl && (
        <a
          href={evidence.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20"
        >
          <ExternalLink className="h-4 w-4" />
          Open in new tab
        </a>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

// Check if evidence can be annotated (PDFs and images)
function isAnnotatable(evidence: Evidence): boolean {
  const ft = (evidence.fileType || '').toLowerCase();
  const fn = (evidence.fileName || '').toLowerCase();
  return ft.includes('pdf') || fn.endsWith('.pdf') ||
         ft.includes('image') || fn.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) !== null;
}

// Wrapper that renders PDFViewer or ImageViewer for annotatable types, falls back to EvidencePreview
function AnnotatablePreview({
  evidence,
  annotations,
  onAnnotationCreate,
  onAnnotationDelete,
  activeTool,
  activeColor,
  rectangleFill,
  readOnly,
}: {
  evidence: Evidence;
  annotations: Annotation[];
  onAnnotationCreate: (a: any) => void;
  onAnnotationDelete: (id: string) => void;
  activeTool: AnnotationTool;
  activeColor: string;
  rectangleFill: boolean;
  readOnly: boolean;
}) {
  const ft = (evidence.fileType || '').toLowerCase();
  const fn = (evidence.fileName || '').toLowerCase();
  const isPdf = ft.includes('pdf') || fn.endsWith('.pdf');
  const isImage = ft.includes('image') || fn.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) !== null;

  const previewUrl = evidence.externalUrl || evidence.thumbnailUrl || '';

  if (isPdf && previewUrl) {
    return (
      <div className="w-full h-full bg-white">
        <PDFViewer
          url={previewUrl}
          annotations={annotations}
          onAnnotationCreate={onAnnotationCreate}
          onAnnotationDelete={onAnnotationDelete}
          activeTool={activeTool}
          activeColor={activeColor}
          rectangleFill={rectangleFill}
          readOnly={readOnly}
        />
      </div>
    );
  }

  if (isImage && previewUrl) {
    return (
      <div className="w-full h-full bg-white">
        <ImageViewer
          url={previewUrl}
          alt={evidence.fileName}
          annotations={annotations}
          onAnnotationCreate={onAnnotationCreate}
          onAnnotationDelete={onAnnotationDelete}
          activeTool={activeTool}
          activeColor={activeColor}
          rectangleFill={rectangleFill}
          readOnly={readOnly}
        />
      </div>
    );
  }

  // Non-annotatable types fall back to the original preview
  return <EvidencePreview evidence={evidence} />;
}

// ─── InsightsPanel ────────────────────────────────────────────────────────────

type InsightTab = 'overview' | 'faces' | 'ocr' | 'objects';

interface InsightsPanelProps {
  loading: boolean;
  insights: any;
  activeTab: InsightTab;
  onTabChange: (tab: InsightTab) => void;
  onClose: () => void;
}

const BADGE: Record<string, string> = {
  vehicle_id:    'bg-blue-100 text-blue-800 border-blue-200',
  route_number:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  phone_number:  'bg-green-100 text-green-800 border-green-200',
  location:      'bg-orange-100 text-orange-800 border-orange-200',
  name:          'bg-purple-100 text-purple-800 border-purple-200',
  default:       'bg-gray-100 text-gray-700 border-gray-200',
};

const TYPE_LABEL: Record<string, string> = {
  vehicle_id:   '🚗 Vehicle ID',
  route_number: '🔢 Route',
  phone_number: '📞 Phone',
  location:     '📍 Location',
  name:         '👤 Name',
};

function InsightsPanel({ loading, insights, activeTab, onTabChange, onClose }: InsightsPanelProps) {
  const tabs: { id: InsightTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'faces',    label: 'People',   icon: <Users className="h-3.5 w-3.5" /> },
    { id: 'ocr',      label: 'Text / OCR', icon: <FileText className="h-3.5 w-3.5" /> },
    { id: 'objects',  label: 'Objects',  icon: <Tag className="h-3.5 w-3.5" /> },
  ];

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PanelHeader onClose={onClose} />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-violet-200 animate-spin border-t-violet-600" />
            <Sparkles className="absolute inset-0 m-auto h-6 w-6 text-violet-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Azure Video Indexer</p>
            <p className="text-sm text-gray-500 mt-1">Extracting intelligence…</p>
          </div>
          <div className="w-full max-w-xs space-y-2 text-left">
            {['Detecting faces & people', 'Reading on-screen text (OCR)', 'Identifying objects & labels', 'Building insight summary'].map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin text-violet-400 flex-shrink-0" />
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="flex flex-col h-full">
        <PanelHeader onClose={onClose} />
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No insights available.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PanelHeader onClose={onClose} />

      {/* Summary strip */}
      <div className="px-4 py-3 bg-violet-50 border-b border-violet-100 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-violet-700">{(insights.faces?.length ?? 0) + (insights.observedPeople?.length ?? 0)}</p>
          <p className="text-xs text-violet-500">People</p>
        </div>
        <div>
          <p className="text-lg font-bold text-violet-700">{insights.detectedObjects?.length > 0 ? insights.detectedObjects.length : (insights.labels?.length ?? 0)}</p>
          <p className="text-xs text-violet-500">Objects</p>
        </div>
        <div>
          <p className="text-lg font-bold text-violet-700">{insights.ocr?.highlights?.length ?? 0}</p>
          <p className="text-xs text-violet-500">Leads</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${
              activeTab === t.id
                ? 'text-violet-700 border-b-2 border-violet-600 bg-white'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
              <Row label="Video duration" value={insights.duration ?? '—'} icon={<Video className="h-3.5 w-3.5 text-gray-400" />} />
              <Row label="Audio" value={insights.audioEffects?.[0] ? `${insights.audioEffects[0].type} (${Math.round((insights.audioEffects[0].ratio ?? 0) * 100)}%)` : '—'} icon={<Mic className="h-3.5 w-3.5 text-gray-400" />} />
              {insights.locations?.length > 0 && (
                <Row label="Location detected" value={insights.locations[0]} icon={<MapPin className="h-3.5 w-3.5 text-orange-500" />} highlight />
              )}
            </div>

            {/* Top investigative leads */}
            {insights.ocr?.highlights?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Investigative Leads</p>
                <div className="space-y-2">
                  {insights.ocr.highlights.map((h: any, i: number) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${BADGE[h.type] ?? BADGE.default}`}>
                      <span className="font-medium">{h.text}</span>
                      <span className="opacity-70">{TYPE_LABEL[h.type] ?? h.type} · {Math.round((h.confidence ?? 0) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Keywords */}
            {insights.keywords?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Keywords</p>
                <div className="flex flex-wrap gap-1.5">
                  {insights.keywords.map((k: string, i: number) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs border border-gray-200">{k}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PEOPLE — named faces + observed people with clothing */}
        {activeTab === 'faces' && (
          <div className="space-y-2">
            {(insights.faces?.length ?? 0) === 0 && (insights.observedPeople?.length ?? 0) === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">No people detected.</p>
            )}
            {insights.faces?.map((f: any, i: number) => (
              <div key={`face-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{f.name}</p>
                  {f.firstSeen && <p className="text-xs text-gray-500">{f.firstSeen} → {f.lastSeen} · {f.seenDuration}s on screen</p>}
                </div>
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded border border-amber-200 font-medium flex-shrink-0">Unidentified</span>
              </div>
            ))}
            {(insights.observedPeople?.length ?? 0) > 0 && (
              <>
                {insights.faces?.length > 0 && (
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1">Observed People (Advanced AI)</p>
                )}
                <div className="grid grid-cols-1 gap-2">
                  {insights.observedPeople.map((p: any, i: number) => {
                    const upper = p.clothing?.find((c: any) => c.type === 'UpperBody');
                    const lower = p.clothing?.find((c: any) => c.type === 'LowerBody');
                    const sleeveLen = upper?.properties?.upperBodyClothingLength;
                    const pantsLen  = lower?.properties?.lowerBodyClothingLength;
                    const firstSeen = p.instances?.[0]?.adjustedStart;
                    const thumbnailUrl = p.thumbnailId && insights.viVideoId
                      ? `/api/evidence/${insights.evidenceId}/vi-thumbnail/${p.thumbnailId}`
                      : null;
                    return (
                      <div key={`op-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-violet-100 bg-violet-50">
                        <div className="w-10 h-10 rounded-lg bg-violet-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {thumbnailUrl
                            ? <img src={thumbnailUrl} alt={`Person ${p.id}`} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display='none'; }} />
                            : <User className="h-5 w-5 text-violet-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">Person #{p.id}</p>
                          <p className="text-xs text-gray-500">
                            {[sleeveLen && `${sleeveLen} sleeve`, pantsLen && `${pantsLen} pants`].filter(Boolean).join(' · ')}
                            {firstSeen && ` · seen at ${firstSeen}`}
                          </p>
                        </div>
                        <span className="px-2 py-0.5 text-xs bg-violet-100 text-violet-700 rounded border border-violet-200 font-medium flex-shrink-0">VI Advanced</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* OCR */}
        {activeTab === 'ocr' && (
          <div className="space-y-4">
            {insights.ocr?.highlights?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Extracted Identifiers</p>
                <div className="space-y-2">
                  {insights.ocr.highlights.map((h: any, i: number) => (
                    <div key={i} className={`flex items-start justify-between p-3 rounded-lg border text-xs gap-2 ${BADGE[h.type] ?? BADGE.default}`}>
                      <div>
                        <p className="font-semibold text-sm">{h.text}</p>
                        <p className="opacity-70 mt-0.5">{TYPE_LABEL[h.type] ?? h.type}</p>
                      </div>
                      <div className="flex-shrink-0 text-right opacity-70">
                        <p>{Math.round((h.confidence ?? 0) * 100)}%</p>
                        <p>confidence</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Full OCR Output</p>
              <div className="bg-gray-900 text-green-400 rounded-lg p-3 text-xs font-mono leading-relaxed break-words">
                {insights.ocr?.fullText || <span className="text-gray-500 italic">No text detected</span>}
              </div>
            </div>
          </div>
        )}

        {/* OBJECTS — detected typed objects (vehicles etc.) + labels */}
        {activeTab === 'objects' && (
          <div className="space-y-4">
            {(insights.detectedObjects?.length ?? 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Detected Objects</p>
                {/* Group by type for a summary row */}
                {(() => {
                  const counts: Record<string, { count: number; wikiDataId: string; items: any[] }> = {};
                  (insights.detectedObjects as any[]).forEach(o => {
                    const key = o.displayName || o.type;
                    if (!counts[key]) counts[key] = { count: 0, wikiDataId: o.wikiDataId, items: [] };
                    counts[key].count++;
                    counts[key].items.push(o);
                  });
                  return Object.entries(counts).map(([name, group]) => {
                    const isBus = name === 'bus' || group.wikiDataId === 'Q5638';
                    const thumbnailOb = group.items.find((o: any) => o.thumbnailId);
                    const thumbnailUrl = thumbnailOb?.thumbnailId && insights.viVideoId
                      ? `/api/evidence/${insights.evidenceId}/vi-thumbnail/${thumbnailOb.thumbnailId}`
                      : null;
                    const firstSeen = group.items[0]?.instances?.[0]?.adjustedStart;
                    return (
                      <div key={name} className={`flex items-center gap-3 p-3 rounded-lg border mb-2 ${isBus ? 'border-orange-200 bg-orange-50' : 'border-blue-100 bg-blue-50'}`}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${isBus ? 'bg-orange-200' : 'bg-blue-200'}`}>
                          {thumbnailUrl
                            ? <img src={thumbnailUrl} alt={name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display='none'; }} />
                            : isBus ? <Bus className="h-5 w-5 text-orange-700" /> : <Car className="h-5 w-5 text-blue-700" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 capitalize">{name} <span className="text-violet-600 font-bold">×{group.count}</span></p>
                          <p className="text-xs text-gray-500">
                            {group.wikiDataId && <span>WikiData: {group.wikiDataId} · </span>}
                            {firstSeen && <span>first seen {firstSeen}</span>}
                          </p>
                        </div>
                        {isBus && <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded border border-orange-200 font-medium flex-shrink-0">⚠ Notable</span>}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            {(insights.labels?.length ?? 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Scene Labels</p>
                <div className="flex flex-wrap gap-2">
                  {insights.labels?.map((l: string, i: number) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-sm bg-white border-gray-200 text-gray-700 font-medium">
                      <Tag className="h-3 w-3 text-gray-400" />{l}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(insights.detectedObjects?.length ?? 0) === 0 && (insights.labels?.length ?? 0) === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">No objects detected.</p>
            )}
          </div>
        )}

      </div>

      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-400">Powered by Azure Video Indexer · Processed {insights.processedAt ? new Date(insights.processedAt).toLocaleString() : '—'}</p>
      </div>
    </div>
  );
}

// ─── AgentAegisPanel ──────────────────────────────────────────────────────────

interface AgentAegisPanelProps {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  input: string;
  loading: boolean;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

function AgentAegisPanel({ messages, input, loading, onInputChange, onSubmit, onClose }: AgentAegisPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-emerald-700 to-teal-600">
        <div className="flex items-center gap-2 text-white">
          <Shield className="h-4 w-4" />
          <span className="text-sm font-semibold">Agent Aegis</span>
          <span className="text-xs text-emerald-200 ml-1">Evidence Intelligence</span>
        </div>
        <button onClick={onClose} className="text-emerald-200 hover:text-white p-1 rounded">
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
              msg.role === 'user' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              <div className="text-sm whitespace-pre-wrap">
                {msg.content.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={j}>{part.slice(2, -2)}</strong>;
                  }
                  return part;
                })}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
              <span className="text-sm text-gray-500">Searching evidence...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about evidence in this case..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            disabled={loading}
          />
          <button
            onClick={onSubmit}
            disabled={!input.trim() || loading}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          🛡️ Agent Aegis answers from this document only
        </p>
      </div>
    </div>
  );
}

function PanelHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-violet-600 to-indigo-600">
      <div className="flex items-center gap-2 text-white">
        <Sparkles className="h-4 w-4" />
        <span className="text-sm font-semibold">Video Intelligence</span>
      </div>
      <button onClick={onClose} className="text-violet-200 hover:text-white p-1 rounded">
        <ChevronLeft className="h-4 w-4" />
      </button>
    </div>
  );
}

function Row({ label, value, icon, highlight = false }: { label: string; value: string; icon?: React.ReactNode; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5">
      {icon}
      <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-xs font-medium ml-auto text-right ${highlight ? 'text-orange-700' : 'text-gray-800'}`}>{value}</span>
    </div>
  );
}

export default EvidenceViewerModal;
